import { useEffect, useRef } from "react";

/* Neural network animation — Form Guru lime (#C8FF00) */
const RGB = "200,255,0";
const CONNECTION_DIST = 200;
const PARTICLE_CHANCE = 0.008;

function createNode(w, h) {
  const speed = 0.16 + Math.random() * 0.4;
  const angle = Math.random() * Math.PI * 2;
  let x = Math.random() * w;
  let y = Math.random() * h;
  const cx = w * 0.5, cy = h * 0.5;
  const inZoneX = x > w * 0.2 && x < w * 0.8;
  const inZoneY = y > h * 0.25 && y < h * 0.75;
  if (inZoneX && inZoneY) {
    const dx = x - cx, dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const push = w * 0.35;
    x = cx + (dx / dist) * push;
    y = cy + (dy / dist) * push;
  }
  return {
    x, y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius: 1.4 + Math.random() * 1.4,
    baseAlpha: 0.20 + Math.random() * 0.10,
    flashPhase: Math.random() * Math.PI * 2,
  };
}

function getNodeCount() {
  if (typeof window === "undefined") return 60;
  return window.innerWidth < 640 ? 25 : 60;
}

export default function HeroCanvas() {
  const canvasRef = useRef(null);
  const nodesRef = useRef([]);
  const particlesRef = useRef([]);
  const animRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function initNodes() {
      const count = getNodeCount();
      nodesRef.current = Array.from({ length: count }, () => createNode(w, h));
      particlesRef.current = [];
    }

    resize();
    initNodes();

    const onResize = () => {
      resize();
      initNodes();
    };
    window.addEventListener("resize", onResize);

    const startTime = performance.now();

    function animate(now) {
      const time = (now - startTime) / 1000;
      ctx.clearRect(0, 0, w, h);

      const nodes = nodesRef.current;
      const particles = particlesRef.current;

      // Update nodes
      for (const node of nodes) {
        node.vx += (Math.random() - 0.5) * 0.02;
        node.vy += (Math.random() - 0.5) * 0.02;
        const spd = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
        if (spd > 0.56) { node.vx *= 0.56 / spd; node.vy *= 0.56 / spd; }
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > w) node.vx *= -1;
        if (node.y < 0 || node.y > h) node.vy *= -1;
        node.x = Math.max(0, Math.min(w, node.x));
        node.y = Math.max(0, Math.min(h, node.y));
      }

      // Connections + spawn particles
      const connDist2 = CONNECTION_DIST * CONNECTION_DIST;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const dist2 = dx * dx + dy * dy;

          if (dist2 < connDist2) {
            const dist = Math.sqrt(dist2);
            const proximity = 1 - dist / CONNECTION_DIST;

            let hasParticle = false;
            for (const p of particles) {
              if (
                (p.fromIdx === i && p.toIdx === j) ||
                (p.fromIdx === j && p.toIdx === i)
              ) {
                hasParticle = true;
                break;
              }
            }

            const lineAlpha = hasParticle
              ? 0.18 + proximity * 0.22
              : 0.05 + proximity * 0.10;

            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(${RGB},${lineAlpha})`;
            ctx.lineWidth = hasParticle ? 1 : 0.6;
            ctx.stroke();

            if (!hasParticle && Math.random() < PARTICLE_CHANCE) {
              particles.push({
                fromIdx: i,
                toIdx: j,
                t: 0,
                speed: 0.006 + Math.random() * 0.01,
              });
            }
          }
        }
      }

      // Update + draw particles
      for (let p = particles.length - 1; p >= 0; p--) {
        const particle = particles[p];
        particle.t += particle.speed;
        if (particle.t >= 1) {
          particles.splice(p, 1);
          continue;
        }
        const from = nodes[particle.fromIdx];
        const to = nodes[particle.toIdx];
        const px = from.x + (to.x - from.x) * particle.t;
        const py = from.y + (to.y - from.y) * particle.t;
        const glowAlpha = 0.5 + Math.sin(particle.t * Math.PI) * 0.2;
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${RGB},${glowAlpha})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(px, py, 7, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${RGB},${glowAlpha * 0.18})`;
        ctx.fill();
      }

      // Draw nodes
      for (const node of nodes) {
        const flash = Math.sin(time * 2 + node.flashPhase);
        const isBright = flash > 0.88;
        const alpha = isBright ? 0.55 : node.baseAlpha;
        const r = isBright ? node.radius * 1.75 : node.radius;
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${RGB},${alpha})`;
        ctx.fill();
        if (isBright) {
          ctx.beginPath();
          ctx.arc(node.x, node.y, r * 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${RGB},0.12)`;
          ctx.fill();
        }
      }

      animRef.current = requestAnimationFrame(animate);
    }

    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
